import { useState } from 'react';
import ScrollReveal from '@/components/feature/ScrollReveal';
import TiltCard from '@/components/feature/TiltCard';
import TypewriterText from '@/components/feature/TypewriterText';

const posts = [
  {
    tag: 'Visa Updates',
    date: '2026-07-20',
    dateDisplay: 'July 20, 2026',
    title: 'Visa Updates 2026: What Indian Travelers Need to Know',
    excerpt: 'Stay informed about the latest visa policy changes for Indian passport holders, including new e-visa destinations and updated documentation requirements for hassle-free international travel.',
    image: 'https://readdy.ai/api/search-image?query=Professional%20travel%20documents%20with%20passport%20and%20visa%20stamps%20on%20elegant%20desk%2C%20premium%20travel%20planning%20aesthetic%2C%20warm%20lighting%2C%20sophisticated%20office%20setting%2C%20world%20map%20background&width=800&height=500&seq=blog-visa&orientation=landscape',
  },
  {
    tag: 'Luxury Holidays',
    date: '2026-07-15',
    dateDisplay: 'July 15, 2026',
    title: 'Top 10 Luxury Honeymoon Destinations for 2026',
    excerpt: 'Discover the most romantic and luxurious honeymoon destinations handpicked by our travel experts for an unforgettable start to married life.',
    image: 'https://readdy.ai/api/search-image?query=Romantic%20luxury%20honeymoon%20suite%20with%20rose%20petals%20and%20ocean%20view%2C%20elegant%20couple%20experience%2C%20premium%20travel%20photography%2C%20soft%20candlelight%2C%20tropical%20sunset%2C%20sophisticated%20interior&width=800&height=500&seq=blog-honeymoon&orientation=landscape',
  },
  {
    tag: 'Family Holidays',
    date: '2026-07-10',
    dateDisplay: 'July 10, 2026',
    title: 'Family Travel Guide: Planning the Perfect Multi-Generational Trip',
    excerpt: 'Tips and insights for planning a seamless family vacation that everyone from grandparents to grandchildren will enjoy and cherish.',
    image: 'https://readdy.ai/api/search-image?query=Happy%20multi%20generational%20family%20enjoying%20luxury%20resort%20vacation%20together%2C%20warm%20family%20bonding%2C%20premium%20travel%20lifestyle%2C%20beautiful%20beach%20setting%2C%20natural%20sunlight%2C%20joyful%20atmosphere&width=800&height=500&seq=blog-family&orientation=landscape',
  },
  {
    tag: 'Cruise Experiences',
    date: '2026-07-05',
    dateDisplay: 'July 5, 2026',
    title: 'Cruise Holidays 2026: The Ultimate Guide to Luxury Cruising',
    excerpt: 'Everything you need to know about luxury cruise holidays — from Mediterranean voyages to Caribbean escapes and Asian river cruises.',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20cruise%20ship%20at%20sunset%20on%20calm%20ocean%20with%20elegant%20deck%20and%20infinity%20pool%2C%20premium%20travel%20experience%2C%20golden%20hour%20lighting%2C%20sophisticated%20lifestyle%2C%20peaceful%20sea%20view&width=800&height=500&seq=blog-cruise&orientation=landscape',
  },
];

function BlogCard({ post, idx }: { post: typeof posts[0]; idx: number }) {
  const [titleDone, setTitleDone] = useState(false);

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: 'Lucky Holidays India Private Limited',
      url: 'https://www.luckyholidays.in/',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Lucky Holidays India Private Limited',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.luckyholidays.in/lucky-holidays-logo.webp',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://www.luckyholidays.in/#blog',
    },
  };

  return (
    <ScrollReveal delay={idx * 120} direction="up" distance={35}>
      <TiltCard maxTilt={5} scale={1.02} glare={true}>
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
        <article
          className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer card-hover-lift"
          itemScope
          itemType="https://schema.org/BlogPosting"
        >
          <div className="relative overflow-hidden aspect-[16/10] img-zoom-on-hover">
            <img
              alt={post.title}
              title={post.title}
              className="w-full h-full object-cover object-top"
              src={post.image}
              loading="lazy"
              itemProp="image"
            />
            <div className="absolute top-3 left-3">
              <span className="px-3 py-1 bg-background-50/90 backdrop-blur-sm text-primary-600 text-[10px] font-semibold rounded-full">
                {post.tag}
              </span>
            </div>
          </div>
          <div className="p-5">
            <meta itemProp="datePublished" content={post.date} />
            <meta itemProp="dateModified" content={post.date} />
            <span className="text-[11px] text-foreground-500">{post.dateDisplay}</span>
            <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mt-2 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2" itemProp="headline">
              <TypewriterText
                text={post.title}
                speed={40}
                onComplete={() => setTitleDone(true)}
              />
            </h3>
            <p className="text-foreground-600 text-xs leading-relaxed line-clamp-2" itemProp="description">
              <TypewriterText
                text={post.excerpt}
                speed={22}
                startWhen={titleDone}
                startDelay={300}
                showCursor={false}
              />
            </p>
            <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary-500 group-hover:text-primary-600 transition-colors link-underline">
              Read More <i className="ri-arrow-right-line text-[10px] group-hover:translate-x-1 transition-transform"></i>
            </span>
          </div>
        </article>
      </TiltCard>
    </ScrollReveal>
  );
}

export default function BlogSection() {
  return (
    <section id="blog" className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-16 3xl:max-w-[1800px] 3xl:mx-auto tv:max-w-[3000px] tv:mx-auto">
        <ScrollReveal>
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-50 text-primary-700 text-xs font-semibold tracking-widest uppercase rounded-full mb-4">
              Travel Insights
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl 3xl:text-6xl font-bold text-foreground-900 mb-4">
              {'Travel Blog & Guides'.split(' ').map((word, i) => (
                <span
                  key={i}
                  className="inline-block mr-[0.3em] animate-text-reveal"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  {word}
                </span>
              ))}
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed animate-text-reveal" style={{ animationDelay: '0.4s' }}>
              Expert travel tips, destination highlights, visa updates and inspiration for your next journey.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 3xl:grid-cols-5 tv:grid-cols-6 gap-5 md:gap-6">
          {posts.map((post, idx) => (
            <BlogCard key={post.title} post={post} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}